import { useCallback } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { AddressMethod, AddressSearchValue } from '@qbe/dfc-smart-ui';
import { AddressList } from '@qbe/dfc-services';
import { ADDRESS_FIELD_MAPS, AddressType } from 'components/AddressSearch/addressHelpers';
import { FormDataType } from 'contexts/FormProvider';
import { AddressChangeParams, ProcessedAddressData, AddressDefaultsParams } from 'types/addressChange';

// Re-export for backward compatibility
export type { ProcessedAddressData };

/**
 * Extracts address defaults from explicit form values for initializing AddressSearch.
 * Used for back-navigation scenarios to restore previous address state.
 *
 * @param type - The address type (physical, postal, repPostal)
 * @param formValues - Current form values provided by parent
 * @returns Address initialization props for AddressSearch component
 */
const getAddressDefaults = ({ type, formValues }: AddressDefaultsParams) => {
  const fieldMap = ADDRESS_FIELD_MAPS[type];
  const isManual = (formValues[fieldMap.isManualAddress] as boolean) ?? false;
  const addressObject = (formValues[fieldMap.addressObject] as AddressList) ?? null;

  const defaultManualAddress = isManual
    ? {
        streetAddress1: (formValues[fieldMap.streetAddress] as string) ?? '',
        city: (formValues[fieldMap.suburb] as string) ?? '',
        state: (formValues[fieldMap.state] as string) ?? '',
        postcode: (formValues[fieldMap.postcode] as string) ?? '',
        country: (formValues[fieldMap.country] as string) ?? '',
        partialAddress: '',
      }
    : null;

  return { isManual, addressObject, defaultManualAddress };
};

/**
 * Hook for managing address form data transformations.
 * Processes address changes from AddressSearch component and returns
 * formatted data ready to be saved to form state.
 *
 * @returns Object containing processAddressChange and getDefaults functions
 *
 * @example
 * ```tsx
 * const { processAddressChange, getDefaults } = useAddressChange();
 * const data = processAddressChange({ addressList, structured, method: AddressMethod.Lookup, type: 'physical' });
 * setValue('phyStreetAddress', data.streetAddress);
 *
 * // For initialization (back navigation)
 * const defaults = getDefaults({ type: 'physical', formValues: watch() });
 * ```
 */
export const useAddressChange = () => {
  // Process address changes and return data for the consumer to set
  const processAddressChange = useCallback(
    ({ addressList, structured, method, type }: AddressChangeParams): ProcessedAddressData => {
      const fieldMap = ADDRESS_FIELD_MAPS[type];

      if (method === AddressMethod.Lookup) {
        const lookupResult: ProcessedAddressData = {
          address: addressList?.partialAddress ?? null,
          addressObject: addressList ?? null,
          streetAddress:
            [structured?.buildingName, structured?.streetAddress1].filter((str) => str && str.length).join(', ') ?? '',
          suburb: structured?.city ?? '',
          state: structured?.state ?? '',
          postcode: structured?.postcode ?? '',
          country: structured?.country ?? 'Australia',
          isAddressValid: !!structured,
          isManualAddress: false,
          ...(fieldMap.isManualAddressValid ? { isManualAddressValid: false } : {}),
        };
        return lookupResult;
      }

      // Manual entry - use provided structured data directly
      // Parent component should ensure complete data is provided
      const resolvedStructured = {
        streetAddress1: structured?.streetAddress1 ?? '',
        city: structured?.city ?? '',
        state: structured?.state ?? '',
        postcode: structured?.postcode ?? '',
        country: structured?.country ?? 'Australia',
      };

      const isValid = !!(
        resolvedStructured.streetAddress1 &&
        resolvedStructured.city &&
        resolvedStructured.state &&
        resolvedStructured.postcode &&
        resolvedStructured.country
      );

      const manualResult: ProcessedAddressData = {
        address: '',
        addressObject: null,
        streetAddress: resolvedStructured.streetAddress1,
        suburb: resolvedStructured.city,
        state: resolvedStructured.state,
        postcode: resolvedStructured.postcode,
        country: resolvedStructured.country,
        isAddressValid: isValid,
        isManualAddress: true,
        ...(fieldMap.isManualAddressValid ? { isManualAddressValid: isValid } : {}),
      };
      return manualResult;
    },
    [] // No dependencies - pure transformation function
  );

  const result = {
    processAddressChange,
    getDefaults: getAddressDefaults,
  };

  return result;
};

/**
 * Creates an address change handler that processes address data and updates form fields.
 * Extracted to avoid code duplication across multiple hooks.
 *
 * @param setValue - React Hook Form setValue function
 * @param processAddressChange - Function to process address changes
 * @returns Handler function for address changes
 *
 * @example
 * ```tsx
 * const { processAddressChange } = useAddressChange();
 * const handleAddressChange = createAddressChangeHandler(setValue, processAddressChange);
 * ```
 */
export const createAddressChangeHandler = (
  setValue: UseFormSetValue<FormDataType>,
  processAddressChange: ReturnType<typeof useAddressChange>['processAddressChange']
) => {
  return (params: Parameters<typeof processAddressChange>[0]) => {
    const data = processAddressChange(params);
    const fieldMap = ADDRESS_FIELD_MAPS[params.type];
    const options = { shouldValidate: true, shouldDirty: true };

    setValue(fieldMap.address, data.address, options);
    setValue(fieldMap.addressObject, data.addressObject, options);
    setValue(fieldMap.streetAddress, data.streetAddress, options);
    setValue(fieldMap.suburb, data.suburb, options);
    setValue(fieldMap.state, data.state, options);
    setValue(fieldMap.postcode, data.postcode, options);
    setValue(fieldMap.country, data.country, options);
    setValue(fieldMap.isAddressValid, data.isAddressValid, options);
    setValue(fieldMap.isManualAddress, data.isManualAddress, options);
    if (fieldMap.isManualAddressValid && data.isManualAddressValid !== undefined) {
      setValue(fieldMap.isManualAddressValid, data.isManualAddressValid, options);
    }
  };
};
